// Ian - 2026-05-20 (price + colour name + slide-mouseover binding added 2026-05-22)
// Linked-product swatch hover preview for Envy product cards.
// Mirrors Envy's native variant swatch behaviour (image swap, price update,
// colour name) so a hovered linked product's data temporarily replaces the
// card's defaults. Native swatches drive this via [data-variant]; linked
// products use <a data-linked-swatch data-primary-image data-linked-price
// data-linked-color-name ...> instead.
//
// Binding strategy:
// Hover handler is bound to the slide (.card-color-swatch--inner) via mouseover
// rather than to the inner <a> via mouseenter. The native ProductCard binds
// mouseover on the same slide, and mouseover fires on every cursor transition
// within the slide (not just on entry). Binding to the slide with mouseover
// guarantees we override the native behaviour on every event, eliminating the
// 'undefined' flash that occurred when the cursor was over slide padding but
// not the anchor.
//
// The script tag for this file uses type="module" defer (see grid-view-item.liquid),
// so it runs after every <product-card> custom element has connected and bound
// its native listeners. Our addEventListener calls therefore happen after
// native's, and our handlers fire after native's on every event, overwriting
// the corrupted output.
//
// Native restore (handleMouseOut) only resets card state when a slide carries
// .active. Linked-product slides use .swatch-selected on the inner <a> instead,
// so the native restore never fires for us. We capture the rendered price,
// colour name, and image state at bind time and restore them on wrapper
// mouseleave.
//
// Recheck after Envy theme updates, especially component-product-card.js
// handleMouseOver / handleMouseOut / updateCardImage / updateCardPrice /
// updateColorName.

(() => {
  const BOUND_ATTR = 'data-linked-swatches-bound';
  const PREVIEW_IMAGE_WIDTH = 900;

  function normaliseImageUrl(src, width = PREVIEW_IMAGE_WIDTH) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.pathname + url.search;
    } catch (error) {
      if (src.includes('width=')) {
        return src.replace(/width=\d+/g, `width=${width}`);
      }

      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}width=${width}`;
    }
  }

  function getMainImage(productCard) {
    return productCard.querySelector('[data-product-card-image]');
  }

  function getSwatchImage(productCard) {
    return productCard.querySelector('img[data-product-card-swatch-image]');
  }

  function getSwatchWrapper(productCard) {
    return productCard.querySelector('.product-card__image--swatch');
  }

  function showLinkedPreview(productCard, src) {
    if (!productCard || !src) return;

    const mainImage = getMainImage(productCard);
    const swatchImage = getSwatchImage(productCard);
    const swatchWrapper = getSwatchWrapper(productCard);

    if (!swatchImage) return;

    const previewSrc = normaliseImageUrl(src);
    swatchImage.removeAttribute('srcset');
    swatchImage.setAttribute('src', previewSrc);
    swatchImage.setAttribute('width', String(PREVIEW_IMAGE_WIDTH));
    swatchImage.setAttribute('height', 'auto');

    swatchImage.style.opacity = '1';
    if (mainImage) mainImage.style.opacity = '0';
    if (swatchWrapper) swatchWrapper.style.opacity = '1';
  }

  function hideLinkedPreview(productCard) {
    if (!productCard) return;

    const mainImage = getMainImage(productCard);
    const swatchImage = getSwatchImage(productCard);
    const swatchWrapper = getSwatchWrapper(productCard);

    if (swatchImage) swatchImage.style.opacity = '0';
    if (mainImage) mainImage.style.opacity = '1';
    if (swatchWrapper) swatchWrapper.style.opacity = '0';
  }

  // Ian - 2026-05-22
  // Capture the rendered price state once at bind time so we can restore
  // exactly what Liquid output (including "from" wording, sale styling, etc.)
  // after a hover. Captured snapshot lives in the closure for this wrapper.
  function captureDefaultPrice(productCard) {
    const sale = productCard.querySelector('.product-price--sale');
    const regular = productCard.querySelector('.product-price--regular');
    if (!sale || !regular) return null;

    return {
      saleHTML: sale.innerHTML,
      regularHTML: regular.innerHTML,
      saleHidden: sale.classList.contains('hidden'),
      regularHidden: regular.classList.contains('hidden'),
    };
  }

  function restoreLinkedPrice(productCard, defaultState) {
    if (!productCard || !defaultState) return;

    const sale = productCard.querySelector('.product-price--sale');
    const regular = productCard.querySelector('.product-price--regular');
    if (!sale || !regular) return;

    sale.innerHTML = defaultState.saleHTML;
    regular.innerHTML = defaultState.regularHTML;
    sale.classList.toggle('hidden', defaultState.saleHidden);
    regular.classList.toggle('hidden', defaultState.regularHidden);
  }

  // Ian - 2026-05-22
  // Mirror native updateCardPrice's show branch (component-product-card.js
  // ~line 10031) so the hovered linked product's price replaces the card price.
  // Reads price + compare-at price (formatted + raw cents) from data-* on the
  // hovered swatch anchor. Runs after the native handler (which corrupted the
  // price to "undefined"), correcting the output.
  function showLinkedPrice(productCard, swatch) {
    if (!productCard || !swatch) return;

    const sale = productCard.querySelector('.product-price--sale');
    const regular = productCard.querySelector('.product-price--regular');
    if (!sale || !regular) return;

    const price = swatch.dataset.linkedPrice;
    if (price == null || price === '') return;

    const priceValue = parseFloat(swatch.dataset.linkedPriceValue);
    const comparePrice = swatch.dataset.linkedCompareAtPrice;
    const comparePriceValueRaw = swatch.dataset.linkedCompareAtPriceValue;
    const comparePriceValue = comparePriceValueRaw ? parseFloat(comparePriceValueRaw) : 0;

    const salePriceEl = sale.querySelector('.price--sale-price');
    const compareAtPriceEl = sale.querySelector('.price--compare-at-price');
    const fromText = productCard.querySelectorAll('.price--from-text');

    if (fromText) fromText.forEach((text) => text.classList.add('hidden'));

    if (!Number.isNaN(comparePriceValue) && comparePriceValue > priceValue && salePriceEl) {
      sale.classList.remove('hidden');
      regular.classList.add('hidden');
      salePriceEl.innerHTML = price;
      if (compareAtPriceEl) compareAtPriceEl.innerHTML = comparePrice;
    } else {
      sale.classList.add('hidden');
      regular.classList.remove('hidden');
      const regularPriceEl = regular.querySelector('.price--regular-price');
      if (regularPriceEl) {
        regularPriceEl.innerHTML = price;
      } else {
        regular.innerHTML = price;
      }
    }
  }

  // Ian - 2026-05-22
  // Colour-name handling. Native updateColorName reads element.dataset.colorValue
  // and bails when missing, so the rendered Liquid label stays unchanged on
  // hover for linked-product slides. We replicate the visible behaviour:
  // overwrite textContent with the hovered swatch's data-linked-color-name on
  // mouseover; restore the captured original on wrapper mouseleave.
  function getColorNameEl(productCard) {
    return productCard.querySelector('[data-product-card-swatch-color-name]');
  }

  function captureDefaultColorName(productCard) {
    const el = getColorNameEl(productCard);
    if (!el) return null;
    return { text: el.textContent };
  }

  function showLinkedColorName(productCard, swatch) {
    if (!productCard || !swatch) return;
    const el = getColorNameEl(productCard);
    if (!el) return;
    const name = swatch.dataset.linkedColorName;
    if (name == null || name === '') return;
    if (el.textContent === name) return;
    el.textContent = name;
  }

  function restoreLinkedColorName(productCard, defaultState) {
    if (!productCard || !defaultState) return;
    const el = getColorNameEl(productCard);
    if (!el) return;
    if (el.textContent === defaultState.text) return;
    el.textContent = defaultState.text;
  }

  function preparePreviewImage(productCard) {
    const swatchImage = getSwatchImage(productCard);
    if (!swatchImage) return;

    const currentSrc = swatchImage.currentSrc || swatchImage.getAttribute('src');
    if (!currentSrc) return;

    swatchImage.setAttribute('src', normaliseImageUrl(currentSrc));
    swatchImage.removeAttribute('srcset');
    swatchImage.setAttribute('width', String(PREVIEW_IMAGE_WIDTH));
    swatchImage.setAttribute('height', 'auto');
  }

  function bindWrapper(wrapper) {
    if (!wrapper || wrapper.hasAttribute(BOUND_ATTR)) return;

    const productCard = wrapper.closest('product-card');
    if (!productCard) return;

    preparePreviewImage(productCard);

    // Ian - 2026-05-22 - Snapshot the rendered price + colour-name state before
    // any hover can mutate it. Used by wrapper mouseleave to restore.
    const defaultPriceState = captureDefaultPrice(productCard);
    const defaultColorNameState = captureDefaultColorName(productCard);

    // Ian - 2026-05-22 - Bind to the slide (.card-color-swatch--inner) with
    // mouseover, not to the inner <a> with mouseenter. Native ProductCard
    // mouseover fires on the slide for every cursor transition (slide entry,
    // moving over the anchor or out of it onto slide padding); binding ours the
    // same way guarantees we override the native 'undefined' write on every
    // event. Each slide carries exactly one [data-linked-swatch] anchor that
    // holds the data attributes for that swatch.
    const slides = wrapper.querySelectorAll('.card-color-swatch--inner');

    slides.forEach((slide) => {
      const swatch = slide.querySelector('[data-linked-swatch]');
      if (!swatch) return;

      slide.addEventListener('mouseover', () => {
        const src = swatch.dataset.primaryImage;
        if (src) showLinkedPreview(productCard, src);
        showLinkedPrice(productCard, swatch);
        showLinkedColorName(productCard, swatch);
      });
    });

    // Ian - 2026-05-22 - Only the wrapper-level mouseleave restores defaults.
    // Per-slide mouseleave would briefly flash the defaults while the cursor
    // crosses the 10px Swiper gap between slides; deferring to wrapper-level
    // means the hovered swatch state persists across in-row movement and only
    // resets when the cursor leaves the swatch row entirely.
    wrapper.addEventListener('mouseleave', () => {
      hideLinkedPreview(productCard);
      restoreLinkedPrice(productCard, defaultPriceState);
      restoreLinkedColorName(productCard, defaultColorNameState);
    });

    wrapper.setAttribute(BOUND_ATTR, 'true');
  }

  function initLinkedSwatchHover() {
    document.querySelectorAll('[data-linked-swatches]').forEach(bindWrapper);
  }

  initLinkedSwatchHover();
  document.addEventListener('DOMContentLoaded', initLinkedSwatchHover);
  document.addEventListener('shopify:section:load', initLinkedSwatchHover);
})();
